<template>
  <v-container fluid class="relative header-space container">
    <v-row class="mb-4">
      <v-col>
        <h1>Dubletten-Kandidat prüfen</h1>
        <v-btn text small :to="{ path: '/konflikte', query: { tab: 'dubletten' } }">
          <v-icon left>mdi-arrow-left</v-icon>Zurück zu Konflikten
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="loading" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="compare">
      <v-row class="mb-2">
        <v-col>
          <v-chip class="mr-2" :color="statusColor(compare.candidate.status)" dark>{{ compare.candidate.status }}</v-chip>
          <span class="caption">Pass: <b>{{ compare.candidate.detectionPass }}</b></span>
          &nbsp;·&nbsp;
          <span class="caption">Ähnlichkeit: <b>{{ compare.candidate.similarity }}%</b></span>
          &nbsp;·&nbsp;
          <span class="caption">Typ: <b>{{ compare.entityType }}</b></span>
        </v-col>
      </v-row>

      <v-alert
        v-for="(warning, index) in compare.warnings"
        :key="'warning-' + index"
        type="warning"
        dense
        outlined
        class="mb-2"
      >
        {{ warning.message }}
      </v-alert>

      <v-alert
        v-if="!followsRecommendation"
        type="info"
        dense
        outlined
        class="mb-2"
      >
        Sie haben von der Empfehlung abgewichen.
        <v-btn x-small text color="primary" class="ml-2" v-on:click="applyRecommendation">
          Empfehlung übernehmen
        </v-btn>
      </v-alert>

      <!-- Summary cards -->
      <v-row align="stretch">
        <template v-for="(side, index) in entitySides">
          <v-col cols="12" md="5" :key="side.key">
            <v-card
              outlined
              :class="cardClasses(side)"
              v-on:click="selectCanonical(side.id)"
              class="summary-card fill-height"
            >
              <div class="summary-card-header pa-4 pb-2" v-on:click.stop="selectCanonical(side.id)">
                <div class="summary-card-header-main">
                  <v-icon
                    :color="isCanonical(side.id) ? 'primary' : 'grey'"
                    class="summary-card-radio"
                  >
                    {{ isCanonical(side.id) ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                  </v-icon>
                  <div class="summary-card-titles">
                    <div class="subtitle-1 font-weight-bold">Datensatz {{ side.letter }}</div>
                    <div class="caption grey--text text--darken-1 mt-1">
                      <v-icon x-small class="mr-1">{{ side.roleIcon }}</v-icon>{{ side.roleLabel }}
                    </div>
                  </div>
                </div>
                <div class="summary-card-badges">
                  <v-chip
                    v-if="isCanonical(side.id)"
                    color="primary"
                    small
                    dark
                    class="mb-1"
                  >
                    Bleibt erhalten
                  </v-chip>
                  <v-chip
                    v-else-if="canonicalId"
                    color="grey"
                    small
                    outlined
                    class="mb-1"
                  >
                    Wird zusammengeführt
                  </v-chip>
                  <v-chip
                    v-if="compare.recommendedCanonicalId === side.id"
                    color="success"
                    small
                    dark
                    class="mb-1"
                  >
                    Empfohlen
                  </v-chip>
                </div>
              </div>
              <v-card-text class="pt-2">
                <div class="text-h6 mb-2 text-truncate" :title="side.displayName">{{ side.displayName }}</div>
                <div class="caption mb-1" v-if="side.entity.createdAt">
                  Erstellt: {{ formatDate(side.entity.createdAt) }}
                </div>
                <div class="caption mb-2" v-if="side.entity.salesforceId">
                  <v-icon x-small class="mr-1">mdi-salesforce</v-icon>{{ side.entity.salesforceId }}
                </div>
                <div class="mb-2">
                  <v-chip v-if="side.entity.isApproved" x-small class="mr-1" color="green" outlined>verifiziert</v-chip>
                  <v-chip v-if="side.entity.excludedByEditor" x-small class="mr-1" color="grey" outlined>ausgeschlossen</v-chip>
                  <v-chip v-if="isAlreadyMerged(side)" x-small color="orange" outlined>bereits zusammengeführt</v-chip>
                </div>
                <div v-if="side.nonZeroCounts.length" class="relation-chips mb-2">
                  <v-chip
                    v-for="item in side.nonZeroCounts"
                    :key="item.label"
                    x-small
                    class="mr-1 mb-1"
                    outlined
                  >
                    {{ item.count }} {{ item.label }}
                  </v-chip>
                </div>
                <div v-else class="caption grey--text mb-2">Keine Verknüpfungen</div>
                <ul
                  v-if="compare.recommendedCanonicalId === side.id && compare.recommendationReasons.length"
                  class="caption mt-1 mb-0 pl-4 recommendation-list"
                >
                  <li v-for="reason in compare.recommendationReasons" :key="reason">{{ reason }}</li>
                </ul>
              </v-card-text>
              <v-card-actions>
                <v-btn text small :to="'/' + compare.entityType + '/' + side.id" target="_blank" rel="noopener noreferrer" v-on:click.stop>
                  <v-icon left>mdi-open-in-new</v-icon>Öffnen
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <!-- Merge direction indicator between A and B -->
          <v-col
            v-if="index === 0"
            :key="side.key + '-merge-arrow'"
            cols="12"
            md="2"
            class="d-flex align-center justify-center merge-arrow-col"
          >
            <div class="text-center merge-arrow" v-if="canonicalId">
              <v-icon large color="primary">{{ mergeArrowIcon }}</v-icon>
              <div class="caption mt-1 merge-arrow-label">
                {{ duplicateSideLetter }}
                <v-icon x-small>mdi-arrow-right</v-icon>
                {{ canonicalSideLetter }}
              </div>
            </div>
          </v-col>
        </template>
      </v-row>

      <!-- Comparison tabs -->
      <v-card outlined class="mt-4">
        <v-tabs v-model="activeTab" background-color="grey lighten-4">
          <v-tab>
            Unterschiede
            <v-chip v-if="diffCount > 0" x-small class="ml-2" color="amber darken-2" dark>{{ diffCount }}</v-chip>
          </v-tab>
          <v-tab>Vollständige Daten</v-tab>
          <v-tab>Verknüpfungen</v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <v-card-text>
              <EntityCompareDiff :field-diffs="compare.fieldDiffs" />
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <p class="overline mb-2">Datensatz A — {{ compare.entityALabel }}</p>
                  <Account
                    v-if="compare.entityType === 'account'"
                    :account="compare.entityA"
                    :inspection-mode="true"
                    :readonly="true"
                    :load-changes="false"
                    root-type="account"
                  />
                  <Contact
                    v-else
                    :contact="compare.entityA"
                    :inspection-mode="true"
                    :readonly="true"
                    :load-changes="false"
                    root-type="contact"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <p class="overline mb-2">Datensatz B — {{ compare.entityBLabel }}</p>
                  <Account
                    v-if="compare.entityType === 'account'"
                    :account="compare.entityB"
                    :inspection-mode="true"
                    :readonly="true"
                    :load-changes="false"
                    root-type="account"
                  />
                  <Contact
                    v-else
                    :contact="compare.entityB"
                    :inspection-mode="true"
                    :readonly="true"
                    :load-changes="false"
                    root-type="contact"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Verknüpfung</th>
                    <th>Datensatz A</th>
                    <th>Datensatz B</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in relationRows"
                    :key="row.key"
                    :class="{ 'relation-diff-row': row.countA !== row.countB }"
                  >
                    <td>{{ row.label }}</td>
                    <td :class="{ 'font-weight-bold': row.countA > row.countB }">{{ row.countA }}</td>
                    <td :class="{ 'font-weight-bold': row.countB > row.countA }">{{ row.countB }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <!-- Actions -->
      <v-card v-if="compare.candidate.status === 'pending'" outlined class="mt-4 pa-4 action-bar">
        <div class="merge-summary text-center mb-4">
          <v-icon color="primary" class="mr-1">mdi-merge</v-icon>
          <span class="body-1">
            <b>Datensatz {{ duplicateSideLetter }}</b> wird in
            <b>Datensatz {{ canonicalSideLetter }}</b> zusammengeführt
          </span>
        </div>
        <v-row>
          <v-col class="text-center">
            <v-btn large class="mr-4 success" :loading="actionLoading" v-on:click="keep">
              <v-icon left>mdi-check</v-icon>Behalten (kein Duplikat)
            </v-btn>
            <v-btn large color="primary" dark :loading="previewLoading" :disabled="!canonicalId" v-on:click="openPreview">
              <v-icon left>mdi-eye</v-icon>Vorschau &amp; Zusammenführen
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-row v-else class="mt-4">
        <v-col class="text-center grey--text">
          Gelöst am {{ formatDate(compare.candidate.resolvedAt) }} von {{ compare.candidate.resolvedBy }}.
        </v-col>
      </v-row>

      <!-- Dry-run preview dialog -->
      <v-dialog v-model="previewDialog" max-width="700" persistent>
        <v-card>
          <v-card-title>Vorschau: Zusammenführen</v-card-title>
          <v-card-text>
            <v-alert type="info" dense outlined class="mb-4">
              Datensatz <b>{{ duplicateSideLetter }}</b> wird in Datensatz <b>{{ canonicalSideLetter }}</b> zusammengeführt.
            </v-alert>
            <div v-if="previewLoading" class="text-center pa-4">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else-if="previewReport">
              <v-alert v-if="previewReport.message" type="info" dense outlined class="mb-3">
                {{ previewReport.message }}
              </v-alert>
              <v-simple-table dense v-if="previewSummaryRows.length">
                <tbody>
                  <tr v-for="row in previewSummaryRows" :key="row.label">
                    <td class="font-weight-medium">{{ row.label }}</td>
                    <td>{{ row.value }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <p v-else class="grey--text">Keine Detailänderungen gemeldet.</p>
            </div>
            <v-alert v-if="previewError" type="error" dense class="mt-3">{{ previewError }}</v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text v-on:click="closePreview">Abbrechen</v-btn>
            <v-btn color="error" :loading="actionLoading" :disabled="!previewReport || previewError" v-on:click="commitMerge">
              Jetzt zusammenführen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top>
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" v-on:click="snackbar.show = false">Schließen</v-btn>
        </template>
      </v-snackbar>
    </div>

    <div v-else class="text-center pa-8 grey--text">
      Kandidat nicht gefunden.
    </div>
  </v-container>
</template>

<script>
import Account from '../components/Account'
import Contact from '../components/Contact'
import EntityCompareDiff from '../components/EntityCompareDiff'

export default {
  components: {
    Account,
    Contact,
    EntityCompareDiff
  },
  props: ['id'],
  data: () => ({
    compare: null,
    canonicalId: null,
    loading: true,
    actionLoading: false,
    previewLoading: false,
    previewDialog: false,
    previewReport: null,
    previewError: null,
    activeTab: 0,
    snackbar: { show: false, message: '', color: 'success' }
  }),
  computed: {
    entitySides () {
      if (!this.compare) return []
      return [
        this.buildSide('A', 'entityA', this.compare.entityAId, this.compare.entityA, this.compare.entityALabel, this.compare.relationCounts.entityA, 'mdi-import'),
        this.buildSide('B', 'entityB', this.compare.entityBId, this.compare.entityB, this.compare.entityBLabel, this.compare.relationCounts.entityB, 'mdi-database')
      ]
    },
    canonicalSideLetter () {
      if (!this.compare || !this.canonicalId) return ''
      return this.canonicalId === this.compare.entityAId ? 'A' : 'B'
    },
    duplicateSideLetter () {
      if (!this.compare || !this.canonicalId) return ''
      return this.canonicalId === this.compare.entityAId ? 'B' : 'A'
    },
    mergeArrowIcon () {
      // A is left, B is right — arrow points from duplicate toward canonical
      return this.canonicalSideLetter === 'A' ? 'mdi-arrow-left-bold' : 'mdi-arrow-right-bold'
    },
    followsRecommendation () {
      return !this.compare || this.canonicalId === this.compare.recommendedCanonicalId
    },
    diffCount () {
      if (!this.compare?.fieldDiffs) return 0
      return this.compare.fieldDiffs.filter(row => row.status !== 'equal').length
    },
    relationRows () {
      if (!this.compare) return []
      const keys = Object.keys(this.compare.relationCounts.entityA)
      return keys.map(key => ({
        key,
        label: this.relationLabel(key),
        countA: this.compare.relationCounts.entityA[key] ?? 0,
        countB: this.compare.relationCounts.entityB[key] ?? 0
      }))
    },
    previewSummaryRows () {
      if (!this.previewReport || !this.previewReport.report) return []
      const rows = []
      const report = this.previewReport.report
      const tables = ['award', 'score', 'messages', 'supplier_identification', 'product', 'account_contact']
      tables.forEach(table => {
        if (!report[table]) return
        const updated = report[table].updated ?? 0
        const deleted = report[table].deleted ?? 0
        if (updated > 0) {
          rows.push({ label: this.relationLabel(table), value: `${updated} verschoben` })
        }
        if (deleted > 0) {
          rows.push({ label: this.relationLabel(table) + ' (Duplikate)', value: `${deleted} entfernt` })
        }
      })
      const entityKey = this.compare?.entityType
      if (entityKey && report[entityKey]?.backfill_columns?.length) {
        rows.push({
          label: 'Backfill-Felder',
          value: report[entityKey].backfill_columns.join(', ')
        })
      }
      if (this.previewReport.repointedTrueRefs > 0) {
        rows.push({
          label: 'Umgeleitete true_reference',
          value: String(this.previewReport.repointedTrueRefs)
        })
      }
      return rows
    }
  },
  methods: {
    statusColor (status) {
      if (status === 'pending') return 'orange'
      if (status === 'merged') return 'error'
      return 'success'
    },
    formatDate (value) {
      if (!value) return ''
      return new Date(value).toLocaleString()
    },
    isCanonical (id) {
      return this.canonicalId === id
    },
    cardClasses (side) {
      return {
        'canonical-selected': this.isCanonical(side.id),
        'duplicate-side': this.canonicalId && !this.isCanonical(side.id)
      }
    },
    buildSide (letter, key, id, entity, roleLabel, counts, roleIcon) {
      let displayName = entity.name
      if (this.compare.entityType === 'contact') {
        displayName = `${entity.firstName || ''} ${entity.surname || ''}`.trim()
      }
      const nonZeroCounts = Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([label, count]) => ({ label: this.relationLabel(label), count }))
      return { letter, key, id, entity, roleLabel, counts, roleIcon, displayName, nonZeroCounts }
    },
    relationLabel (key) {
      const labels = {
        awards: 'Awards',
        scores: 'Scores',
        messages: 'Nachrichten',
        contacts: 'Kontakte',
        products: 'Produkte',
        accounts: 'Accounts',
        award: 'Awards',
        score: 'Scores',
        supplier_identification: 'Supplier-IDs',
        product: 'Produkte',
        account_contact: 'Account-Kontakt-Verknüpfungen'
      }
      return labels[key] || key
    },
    isAlreadyMerged (side) {
      if (!this.compare?.warnings) return false
      return this.compare.warnings.some(warning => warning.code === 'ALREADY_MERGED' && warning.side === side.key)
    },
    selectCanonical (id) {
      this.canonicalId = id
    },
    applyRecommendation () {
      if (!this.compare) return
      this.canonicalId = this.compare.recommendedCanonicalId
    },
    async keep () {
      this.actionLoading = true
      try {
        const response = await this.$http.post(`/duplicate-candidate/${this.id}/keep`)
        this.compare.candidate = response.data
        this.showSnackbar('Als "behalten" markiert.', 'success')
      } catch (e) {
        this.showSnackbar('Fehler: ' + (e.response?.data?.error || e.message), 'error')
      } finally {
        this.actionLoading = false
      }
    },
    async openPreview () {
      this.previewDialog = true
      this.previewLoading = true
      this.previewReport = null
      this.previewError = null
      try {
        const response = await this.$http.post(`/duplicate-candidate/${this.id}/merge`, {
          canonicalEntityId: this.canonicalId,
          dryRun: true
        })
        this.previewReport = response.data
      } catch (e) {
        this.previewError = e.response?.data?.error || e.message
      } finally {
        this.previewLoading = false
      }
    },
    closePreview () {
      this.previewDialog = false
      this.previewReport = null
      this.previewError = null
    },
    async commitMerge () {
      this.actionLoading = true
      try {
        const response = await this.$http.post(`/duplicate-candidate/${this.id}/merge`, {
          canonicalEntityId: this.canonicalId,
          dryRun: false
        })
        this.compare.candidate = response.data.candidate
        this.closePreview()
        this.showSnackbar('Entitäten zusammengeführt.', 'success')
      } catch (e) {
        this.previewError = e.response?.data?.error || e.message
        this.showSnackbar('Fehler: ' + (e.response?.data?.error || e.message), 'error')
      } finally {
        this.actionLoading = false
      }
    },
    showSnackbar (message, color) {
      this.snackbar = { show: true, message, color }
    }
  },
  async created () {
    await this.$auth.waitForAuth()
    if (!this.$auth.isAuthenticated) {
      this.$router.push('/')
      return
    }
    try {
      const response = await this.$http.get(`/duplicate-candidate/${this.id}/compare`)
      this.compare = response.data
      this.canonicalId = this.compare.recommendedCanonicalId
    } catch (e) {
      this.compare = null
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.summary-card {
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.summary-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.summary-card-header-main {
  display: flex;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
}
.summary-card-radio {
  flex-shrink: 0;
  margin-top: 2px;
}
.summary-card-titles {
  margin-left: 8px;
  min-width: 0;
}
.summary-card-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
}
.canonical-selected {
  border: 2px solid #1976d2 !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
}
.duplicate-side {
  opacity: 0.85;
  border-style: dashed !important;
}
.merge-arrow-col {
  min-height: 80px;
}
.merge-arrow-label {
  font-weight: 600;
  color: #1976d2;
}
.recommendation-list {
  color: #2e7d32;
}
.action-bar {
  position: sticky;
  bottom: 16px;
  background: white;
  z-index: 2;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}
.merge-summary {
  padding: 8px 16px;
  background: #e3f2fd;
  border-radius: 4px;
}
</style>

<style>
.relation-diff-row {
  background-color: #fff8e1 !important;
}
</style>
